// Programs-list logic shared by every surface that shows the caller's
// programs (mobile Plan tab, web-rnw console list). Pure TS — the React
// hook lives in @heraion/ui (usePrograms); this module owns the
// parse + classification rules so they can't drift between apps
// (anti-monster S-B).

import {
  Program,
  ProgramProgress,
  ProgramSummary,
  type Program as ProgramT,
  type ProgramProgress as ProgramProgressT,
  type ProgramSummary as ProgramSummaryT,
} from "./schemas/program.js";

/** Parse the GET /api/me/programs payload. Per-item zod validation:
 *  one malformed row is dropped instead of poisoning the whole list
 *  (same posture as the catalog and feedback parsers). */
export const parseProgramsList = (raw: unknown): ProgramT[] => {
  if (!Array.isArray(raw)) return [];
  const out: ProgramT[] = [];
  for (const item of raw) {
    const parsed = Program.safeParse(item);
    if (parsed.success) out.push(parsed.data);
  }
  return out;
};

/** A row of a coach's read of an athlete's programs: the full program, or
 *  only its summary when another coach wrote it (#332). */
export type ProgramRow = ProgramT | ProgramSummaryT;

export const isFullProgram = (p: ProgramRow): p is ProgramT => "sessions" in p;

/** The athlete's pinned program as a coach gets it: `row` names it,
 *  `program` is set only when its content is readable (not a summary). */
export const findPinnedProgram = (
  programs: ReadonlyArray<ProgramRow>,
  currentProgramId: string | undefined,
): { row: ProgramRow | null; program: ProgramT | null } => {
  const row = currentProgramId
    ? (programs.find((p) => p.id === currentProgramId) ?? null)
    : null;
  return { row, program: row && isFullProgram(row) ? row : null };
};

/** Parse GET /api/me/programs?athleteSub=. Keeps the summary rows that
 *  parseProgramsList drops: the coach can name those programs, not open
 *  them. A malformed row is dropped, as there. */
export const parseProgramSummaries = (raw: unknown): ProgramRow[] => {
  if (!Array.isArray(raw)) return [];
  const out: ProgramRow[] = [];
  for (const item of raw) {
    const full = Program.safeParse(item);
    if (full.success) {
      out.push(full.data);
      continue;
    }
    const summary = ProgramSummary.safeParse(item);
    if (summary.success) out.push(summary.data);
  }
  return out;
};

/** Parse one GET /api/me/programs/:id/progress payload. null means
 *  "never run" (the server returns null) or schema drift. */
export const parseProgramProgress = (raw: unknown): ProgramProgressT | null => {
  if (raw === null || raw === undefined) return null;
  const parsed = ProgramProgress.safeParse(raw);
  return parsed.success ? parsed.data : null;
};

/** Follow+Publish model (next/archive/programs-follow-model.md, 2026-06-12):
 *  the 3 stored statuses are two orthogonal axes — lifecycle
 *  (draft → published) and follow (followed / not). UI words:
 *    - followed  (status=active):    "In corso" — the only startable bucket.
 *    - published (status=archived):  published, not followed ("Pubblicato").
 *    - drafts    (status=draft):     not followable/startable yet ("Bozza").
 *  The old split kept drafts runnable; that made drafts startable from
 *  every start surface and was part of the active/deactivated/draft
 *  confusion this model replaces. */
export const splitProgramsByStatus = (
  programs: ReadonlyArray<ProgramT>,
  /** Ids of programs the caller FOLLOWS: a pointer to a row that lives in
   *  its author's partition (next/coaching.md, "La SPEC del follower" §2).
   *  Their `status` is the AUTHOR's, so it says nothing about the follower:
   *  a program the author left "archived" would land under Published, which
   *  the follower cannot publish or unpublish. The follow decides the
   *  bucket instead, and it is always "followed".
   *  Empty (the default) ⇒ every program is classified by status exactly as
   *  before. */
  followedIds: ReadonlySet<string> = new Set(),
): { followed: ProgramT[]; published: ProgramT[]; drafts: ProgramT[] } => {
  const followed: ProgramT[] = [];
  const published: ProgramT[] = [];
  const drafts: ProgramT[] = [];
  for (const p of programs) {
    if (followedIds.has(p.id)) followed.push(p);
    else if (p.status === "active") followed.push(p);
    else if (p.status === "archived") published.push(p);
    else if (p.status === "draft") drafts.push(p);
  }
  return { followed, published, drafts };
};
